import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApolloFiltersEditor } from "../components/ApolloFiltersEditor";
import { Divider } from "../components/Divider";
import { IcpSampleManager } from "../components/IcpSampleManager";
import { StatusLine, type StatusVariant } from "../components/StatusLine";
import { useIcps, useUpdateIcp } from "../hooks/useIcps";
import type {
  ApolloFilters,
  BreakdownRubric,
  Icp,
  IcpUpdate,
} from "../lib/types";

interface FormState {
  display_name: string;
  description: string;
  context: string;
  prompt: string;
  lemlist_campaign_id: string;
  min_score: string;
  is_active: boolean;
  breakdown_rubric: string;
  search_filters: ApolloFilters;
}

function toForm(icp: Icp): FormState {
  return {
    display_name: icp.display_name,
    description: icp.description ?? "",
    context: icp.context ?? "",
    prompt: icp.prompt ?? "",
    lemlist_campaign_id: icp.lemlist_campaign_id ?? "",
    min_score: String(icp.min_score),
    is_active: icp.is_active,
    breakdown_rubric: JSON.stringify(icp.breakdown_rubric, null, 2),
    search_filters: icp.search_filters,
  };
}

function parseJsonObject(label: string, raw: string): Record<string, unknown> {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`${label}: invalid JSON — ${msg}`);
  }
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    throw new Error(`${label}: must be a JSON object.`);
  }
  return value as Record<string, unknown>;
}

function buildPayload(icp: Icp, form: FormState): IcpUpdate {
  const display_name = form.display_name.trim();
  if (!display_name) throw new Error("display_name: cannot be empty.");

  const context = form.context.trim();
  if (!context) throw new Error("context: cannot be empty.");

  const min_score = Number(form.min_score);
  if (!Number.isInteger(min_score) || min_score < 0 || min_score > 100) {
    throw new Error("min_score: must be an integer between 0 and 100.");
  }

  const rubric = parseJsonObject("breakdown_rubric", form.breakdown_rubric);

  return {
    name: icp.name,
    display_name,
    description: form.description.trim() || null,
    context,
    prompt: form.prompt,
    lemlist_campaign_id: form.lemlist_campaign_id.trim() || null,
    min_score,
    is_active: form.is_active,
    // ``rubric`` is user-typed JSON; structural validation runs server-side
    // in ``ai_service.validate_breakdown_rubric``.
    breakdown_rubric: rubric as BreakdownRubric,
    search_filters: form.search_filters,
  };
}

export function IcpEdit() {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { data: icps, isLoading, isError, error } = useIcps({
    includeInactive: true,
  });
  const update = useUpdateIcp();

  const icp = useMemo(
    () => icps?.find((i) => i.name === name),
    [icps, name],
  );

  const [form, setForm] = useState<FormState | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (icp) setForm(toForm(icp));
  }, [icp]);


  if (isLoading) {
    return (
      <section className="card">
        <StatusLine variant="running" message="Loading…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="card">
        <StatusLine
          variant="error"
          message={`Failed to load: ${error?.message ?? "unknown"}`}
        />
      </section>
    );
  }

  if (!icp || !form) {
    return (
      <section className="card">
        <h2>ICP not found</h2>
        <p className="card-desc">No ICP named <code>{name}</code>.</p>
        <Link to="/icps">Back to list</Link>
      </section>
    );
  }

  const onSave = async () => {
    setValidationError(null);
    let payload: IcpUpdate;
    try {
      payload = buildPayload(icp, form);
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : String(err));
      return;
    }
    try {
      await update.mutateAsync(payload);
    } catch {
      // ``update.error`` is rendered via the StatusLine below — nothing
      // more to do here.
      return;
    }
    enqueueSnackbar(`Saved “${icp.display_name}”.`, { variant: "success" });
    navigate("/icps", { replace: true });
  };

  const status = computeStatus({
    pending: update.isPending,
    success: update.isSuccess,
    error: validationError ?? update.error?.message ?? null,
  });

  return (
    <section className="card">
      <h2>Edit ICP</h2>
      <p className="breadcrumb">
        <Link to="/icps">← All ICPs</Link>
        {" · "}
        <Link to={`/icps/${icp.name}/improve`}>Improve prompt</Link>
      </p>

      <div className="field-row">
        <div className="field">
          <label htmlFor="icp-name">Name (read-only)</label>
          <input
            id="icp-name"
            type="text"
            value={icp.name}
            readOnly
            disabled
          />
        </div>
        <div className="field">
          <label htmlFor="icp-display">Display name</label>
          <input
            id="icp-display"
            type="text"
            value={form.display_name}
            onChange={(e) =>
              setForm({ ...form, display_name: e.target.value })
            }
          />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="icp-min-score">Min score (0–100)</label>
          <input
            id="icp-min-score"
            type="number"
            min={0}
            max={100}
            value={form.min_score}
            onChange={(e) => setForm({ ...form, min_score: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="icp-lemlist">Lemlist campaign ID</label>
          <input
            id="icp-lemlist"
            type="text"
            value={form.lemlist_campaign_id}
            onChange={(e) =>
              setForm({ ...form, lemlist_campaign_id: e.target.value })
            }
            placeholder="(none)"
          />
        </div>
      </div>

      <div className="checkbox-field">
        <label>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) =>
              setForm({ ...form, is_active: e.target.checked })
            }
          />{" "}
          Active
        </label>
      </div>

      <div className="field">
        <label htmlFor="icp-description">
          Description{" "}
          <span className="field-help" title="Free-form notes for the team. Not sent to the AI.">
            ⓘ
          </span>
        </label>
        <textarea
          id="icp-description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={3}
          placeholder="What this ICP targets — short notes for the team."
        />
      </div>

      <div className="field">
        <label htmlFor="icp-context">
          Context (AI-facing){" "}
          <span
            className="field-help"
            title="Fed to the scoring-prompt author on every iteration (v1 + every rewrite) as the stable intent of this ICP."
          >
            ⓘ
          </span>
        </label>
        <textarea
          id="icp-context"
          value={form.context}
          onChange={(e) => setForm({ ...form, context: e.target.value })}
          rows={10}
          placeholder="Who does this ICP target? Segment, size band, geography, key signals."
        />
      </div>

      <div className="field">
        <label htmlFor="icp-prompt">System prompt</label>
        <textarea
          id="icp-prompt"
          value={form.prompt}
          onChange={(e) => setForm({ ...form, prompt: e.target.value })}
          rows={26}
          style={{ fontFamily: "monospace" }}
        />
      </div>

      <div className="field">
        <label htmlFor="icp-rubric">Breakdown rubric (JSON)</label>
        <details className="field-hint">
          <summary>How does this work?</summary>
          <p>
            Each <strong>axis</strong> in the rubric is one scoring criterion
            the AI evaluates for every contact (e.g. industry fit, company
            size, compliance signal). The AI returns a number per axis; those
            are summed and combined with the AI's holistic 0–100 estimate to
            produce the final ICP fit score.
          </p>
          <p>An axis is configured in one of two shapes:</p>
          <ul>
            <li>
              <strong>Discrete buckets</strong> — the AI must pick one of the
              listed numbers. Use this when each level has a fixed weight.
              <br />
              <code>{'"icp_segment_fit": {"values": [0, 20, 45]}'}</code>
            </li>
            <li>
              <strong>Inclusive range</strong> — the AI picks any integer
              between <code>min</code> and <code>max</code>.
              <br />
              <code>{'"data_quality_signal": {"min": -5, "max": 30}'}</code>
            </li>
          </ul>
          <p>
            <strong>Special axis name:</strong>{" "}
            <code>no_go_penalty</code> is treated as a dealbreaker. Any
            negative value the AI returns snaps to the most-negative bucket
            you configured; anything ≥ 0 snaps to 0. Use it to enforce hard
            disqualifiers (e.g. wrong region, blocklisted industry).
          </p>
          <p>
            Leave the rubric as <code>{`{}`}</code> to skip the breakdown
            entirely — the AI then returns only a holistic 0–100 score with
            a rationale. Good for quick experimental ICPs.
          </p>
          <p>
            All axis names must be unique snake_case strings; they're sent to
            the AI as field names, so make them descriptive.
          </p>
          <p>
            <strong>Important:</strong> every axis you add here must also be
            explained in the system prompt above — what it measures, when to
            pick each bucket / value, and what evidence to look for. The AI
            sees only the axis name; without prompt guidance it will guess.
          </p>
        </details>
        <textarea
          id="icp-rubric"
          value={form.breakdown_rubric}
          onChange={(e) =>
            setForm({ ...form, breakdown_rubric: e.target.value })
          }
          rows={15}
          style={{ fontFamily: "monospace" }}
        />
      </div>

      <div className="field">
        <label>Apollo search filters</label>
        <ApolloFiltersEditor
          value={form.search_filters}
          onChange={(next) =>
            setForm({ ...form, search_filters: next })
          }
        />
      </div>

      <Divider />
      <p className="card-desc" style={{ marginTop: 16 }}>
        <strong>Note:</strong> sample changes below save immediately when you
        add, edit, or remove a contact — the page-level <em>Save</em> button
        does not apply to them.
      </p>
      <IcpSampleManager icpName={icp.name} />

      <button onClick={onSave} disabled={update.isPending}>
        {update.isPending ? "Saving…" : "Save"}
      </button>

      <StatusLine variant={status.variant} message={status.message} />
    </section>
  );
}

function computeStatus(args: {
  pending: boolean;
  success: boolean;
  error: string | null;
}): { variant: StatusVariant; message: string } {
  if (args.pending) return { variant: "running", message: "Saving…" };
  if (args.error) return { variant: "error", message: args.error };
  if (args.success) return { variant: "success", message: "Saved." };
  return { variant: "idle", message: "" };
}
